import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { io } from 'socket.io-client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Ship, 
  MapPin,
  Wifi,
  WifiOff,
  Search,
  Loader2,
  Bell,
  Key,
  History,
  Activity,
  Unplug,
  Cpu,
  RefreshCw,
  Settings2,
  LayoutGrid,
  Cloud,
  Copy,
  Check,
  FolderOpen,
  Package,
  Hexagon,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { showErrorToast } from '@/utils/errorHandler';
import MQTTCredentialsModal from '@/components/mqtt/MQTTCredentialsModal';
import ProvisioningModal from '@/components/provisioning/ProvisioningModal';
import ProvisioningWizard from '@/components/ProvisioningWizard';
import GPSSourceSelector from '@/components/gps/GPSSourceSelector';
import AgentUpdateModal, { AgentUpdateBadge } from '@/components/agent/AgentUpdateModal';
import EquipmentGeofenceConfig from '@/components/EquipmentGeofenceConfig';
import MotorsConfig from '@/components/equipments/MotorsConfig';
import SignalsConfig from '@/components/equipments/SignalsConfig';
import ClassifierSettings from '@/components/equipments/ClassifierSettings';
import TankConfig from '@/components/equipments/TankConfig';
import { useTimezone } from '@/contexts/TimezoneContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom marker for location picker
const locationMarkerIcon = L.divIcon({
  className: 'location-picker-marker',
  html: `
    <div style="
      width: 30px;
      height: 30px;
      background: rgba(14, 165, 233, 0.3);
      border: 3px solid #0ea5e9;
      border-radius: 50%;
      box-shadow: 0 0 15px rgba(14, 165, 233, 0.5);
    "></div>
  `,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

// Location picker map component
const LocationPicker = ({ position, onPositionChange }) => {
  const { t } = useTranslation();
  
  const MapClickHandler = () => {
    useMapEvents({
      click: (e) => {
        onPositionChange([e.latlng.lat, e.latlng.lng]);
      },
    });
    return null;
  };

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{t('setLocationOnMap')}</p>
      <div className="h-64 rounded-lg overflow-hidden border border-border">
        <MapContainer
          center={position || [-23.5505, -46.6333]}
          zoom={10}
          className="h-full w-full"
          style={{ background: '#020617' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          <MapClickHandler />
          {position && (
            <Marker position={position} icon={locationMarkerIcon} />
          )}
        </MapContainer>
      </div>
    </div>
  );
};

const EquipmentsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandledRef = useRef(false);
  const { formatDateTime, formatDate } = useTimezone();
  const [equipments, setEquipments] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [opStatusFilter, setOpStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mqttCredentialsEquipment, setMqttCredentialsEquipment] = useState(null);
  const [provisioningEquipment, setProvisioningEquipment] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [reprovisioningEquipment, setReprovisioningEquipment] = useState(null);
  const [gpsSourceEquipment, setGpsSourceEquipment] = useState(null); // v3.10.0: GPS Source Selector
  const [agentUpdateEquipment, setAgentUpdateEquipment] = useState(null); // v3.10.0: Agent Update Modal
  const [geofenceEquipment, setGeofenceEquipment] = useState(null); // v3.11.0: Geofence Config
  // Phase 999.4 Plan 03 (D-01/D-02): diálogo Editar Equipamento em abas —
  // sempre reabre em "Identificação"; cada aba tem seu próprio Salvar.
  const [activeTab, setActiveTab] = useState('identificacao');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    equipment_type_id: '',
    location_type: 'gps',
    latitude: null,
    longitude: null,
    tag_senior: '',
    description: '',
    registration: '',
    erp_integration_id: '',
    operational_status: 'active',
    no_telemetry: false,
    // Modos de carga da embarcação (CC-2026-07-02-005): [{id,label,capacidade_t}] —
    // alimentam o dropdown de modo e o volume previsto na programação de viagens.
    cargo_modes: [],
  });

  const fetchData = useCallback(async () => {
    try {
      const [eqResponse, typesResponse] = await Promise.all([
        axios.get(`${API}/equipments`),
        axios.get(`${API}/equipment-types`),
      ]);
      setEquipments(eqResponse.data);
      setEquipmentTypes(typesResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Polling para equipamentos em awaiting_connection
  // Verifica a cada 10 segundos se algum equipamento mudou de status
  useEffect(() => {
    const hasAwaiting = equipments.some(eq => eq.status === 'awaiting_connection');
    if (!hasAwaiting) return;
    
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/equipments`);
        const newEquipments = response.data;
        
        // Verificar se algum equipamento saiu do status awaiting_connection
        const previousAwaiting = equipments.filter(eq => eq.status === 'awaiting_connection');
        const nowOnline = previousAwaiting.filter(prevEq => {
          const newEq = newEquipments.find(e => e.id === prevEq.id);
          return newEq && newEq.status !== 'awaiting_connection';
        });
        
        if (nowOnline.length > 0) {
          nowOnline.forEach(eq => {
            toast.success(`${eq.name} conectou! Agora você pode finalizar o provisionamento.`);
          });
          setEquipments(newEquipments);
        }
      } catch (error) {
        // Silently fail - don't show error for polling
        console.debug('Polling awaiting_connection:', error.message);
      }
    }, 10000); // Poll every 10 seconds
    
    return () => clearInterval(interval);
  }, [equipments]);

  // FIX: Socket.IO listener para equipment_status (atualização em tempo real)
  useEffect(() => {
    const socket = io(API.replace('/api', ''), {
      transports: ['websocket', 'polling'],
    });
    
    const handleEquipmentStatus = (statusUpdate) => {
      if (!statusUpdate?.equipment_id && !statusUpdate?.tag) return;
      
      setEquipments(prev => {
        return prev.map(eq => {
          const isMatch = 
            (statusUpdate.equipment_id && eq.id === statusUpdate.equipment_id) ||
            (statusUpdate.tag && eq.tag?.toUpperCase() === statusUpdate.tag?.toUpperCase());
          
          if (isMatch) {
            console.log(`[Socket] Equipment status: ${eq.tag} -> ${statusUpdate.status}`);
            return {
              ...eq,
              status: statusUpdate.status || eq.status,
              last_seen: statusUpdate.timestamp || eq.last_seen,
              agent_version: statusUpdate.agent_version || eq.agent_version,
            };
          }
          return eq;
        });
      });
    };
    
    socket.on('equipment_status', handleEquipmentStatus);
    
    return () => {
      socket.off('equipment_status', handleEquipmentStatus);
      socket.disconnect();
    };
  }, []);

  const handleOpenDialog = (equipment = null) => {
    if (equipment) {
      setFormData({
        name: equipment.name,
        equipment_type_id: equipment.equipment_type_id,
        location_type: equipment.location_type,
        latitude: equipment.latitude,
        longitude: equipment.longitude,
        tag_senior: equipment.tag_senior || '',
        description: equipment.description || '',
        registration: equipment.registration || '',
        erp_integration_id: equipment.erp_integration_id || '',
        operational_status: equipment.operational_status || 'active',
        no_telemetry: !!equipment.no_telemetry,
        cargo_modes: Array.isArray(equipment.cargo_modes) ? equipment.cargo_modes : [],
      });
      setSelectedEquipment(equipment);
    } else {
      setFormData({
        name: '',
        equipment_type_id: '',
        location_type: 'gps',
        latitude: null,
        longitude: null,
        tag_senior: '',
        description: '',
        registration: '',
        erp_integration_id: '',
        operational_status: 'active',
        no_telemetry: false,
        cargo_modes: [],
      });
      setSelectedEquipment(null);
    }
    setActiveTab('identificacao');
    setDialogOpen(true);
  };

  // Deep-link "Editar na aba Motores" (Fase 1000, Plan 06): a página de
  // alertas do equipamento (`/equipments/:id/alerts`) navega para cá com
  // `?edit={id}&tab=motores` — abre o diálogo de edição já na aba correta
  // assim que a lista carregar, uma única vez, e limpa a query (replace) para
  // não reabrir em navegações futuras (voltar/avançar do navegador).
  useEffect(() => {
    if (deepLinkHandledRef.current || loading || equipments.length === 0) return;
    const editId = searchParams.get('edit');
    if (!editId) return;
    deepLinkHandledRef.current = true;
    const target = equipments.find((eq) => eq.id === editId);
    if (target) {
      handleOpenDialog(target);
      const tab = searchParams.get('tab');
      if (tab) setActiveTab(tab);
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, equipments, searchParams]);

  // D-18: gate da aba Tanques — usa a flag `uses_fuel` do TIPO do equipamento em
  // edição (mesmo padrão de filtro por tipo do Plan 02, sem hardcode de nome).
  const selectedEquipmentType = selectedEquipment
    ? equipmentTypes.find((t) => t.id === (selectedEquipment.equipment_type_id || formData.equipment_type_id))
    : equipmentTypes.find((t) => t.id === formData.equipment_type_id);
  const usesFuel = selectedEquipmentType?.uses_fuel === true;

  // Sanitiza os modos de carga: descarta linhas sem label; id = slug estável do label;
  // capacidade vira número (ou null). O id alimenta planned.modo_carga nas viagens.
  const _sanitizeCargoModes = (modes) =>
    (modes || [])
      .filter((m) => (m.label || '').trim())
      .map((m) => {
        const label = m.label.trim();
        const cap = Number(m.capacidade_t);
        return {
          id: label.normalize('NFKD').replace(/[̀-ͯ]/g, '')
            .replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '').toLowerCase() || 'modo',
          label,
          capacidade_t: Number.isFinite(cap) && cap > 0 ? cap : null,
        };
      });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...formData, cargo_modes: _sanitizeCargoModes(formData.cargo_modes) };
      if (selectedEquipment) {
        await axios.put(`${API}/equipments/${selectedEquipment.id}`, payload);
        toast.success(t('saveSuccess'));
      } else {
        await axios.post(`${API}/equipments`, payload);
        toast.success(t('saveSuccess'));
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving equipment:', error);
      showErrorToast(toast, error, t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEquipment) return;
    
    try {
      const response = await axios.delete(`${API}/equipments/${selectedEquipment.id}`);
      
      // Verificar resposta de sucesso (pode ser 200 ou 204)
      if (response.status >= 200 && response.status < 300) {
        toast.success(t('deleteSuccess'));
        // Atualizar state local imediatamente (otimista)
        setEquipments(prev => prev.filter(eq => eq.id !== selectedEquipment.id));
        setDeleteDialogOpen(false);
        setSelectedEquipment(null);
      }
    } catch (error) {
      console.error('Error deleting equipment:', error);
      showErrorToast(toast, error, t('errorOccurred'));
      // Recarregar dados para garantir consistência
      fetchData();
    }
  };

  const handleUnlinkDevice = async () => {
    if (!selectedEquipment) return;
    try {
      await axios.post(`${API}/equipments/${selectedEquipment.id}/unlink-device`);
      toast.success(`Dispositivo desvinculado de ${selectedEquipment.name}`);
      setUnlinkDialogOpen(false);
      setSelectedEquipment(null);
      fetchData();
    } catch (error) {
      console.error('Error unlinking device:', error);
      showErrorToast(toast, error, 'Erro ao desvincular dispositivo');
    }
  };

  // Separar equipamentos ativos dos que aguardam conexão
  const allFiltered = equipments.filter(eq => {
    const matchesSearch = eq.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      eq.tag.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (eq.registration && eq.registration.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesOpStatus = opStatusFilter === 'all' || eq.operational_status === opStatusFilter;
    return matchesSearch && matchesOpStatus;
  });
  
  const filteredEquipments = allFiltered.filter(eq => eq.status !== 'awaiting_connection');
  const awaitingConnectionEquipments = allFiltered.filter(eq => eq.status === 'awaiting_connection');

  return (
    <div className="page-container space-y-6" data-testid="equipments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="pl-12 md:pl-0">
          <h1 className="text-3xl font-bold tracking-tight">{t('equipments')}</h1>
          <p className="text-muted-foreground">Gerencie seus equipamentos IoT</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/equipment-groups')} className="gap-2" data-testid="equipment-groups-btn">
            <FolderOpen className="w-4 h-4" />
            Grupos
          </Button>
          <Button onClick={() => setWizardOpen(true)} className="gap-2" data-testid="provision-new-btn">
            <Plus className="w-4 h-4" />
            Provisionar Novo
          </Button>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="equipment-search"
          />
        </div>
        <Select value={opStatusFilter} onValueChange={setOpStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="op-status-filter">
            <SelectValue placeholder="Status Operacional" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Ativo</SelectItem>
            <SelectItem value="maintenance">Manutenção</SelectItem>
            <SelectItem value="inactive">Inativo</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('equipmentName')}</TableHead>
                  <TableHead>{t('equipmentTag')}</TableHead>
                  <TableHead>Registro</TableHead>
                  <TableHead>{t('equipmentType')}</TableHead>
                  <TableHead>{t('status')}</TableHead>
                  <TableHead>Operacional</TableHead>
                  <TableHead className="text-right">{t('edit')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : filteredEquipments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      <Ship className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      {t('noEquipments')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEquipments.map((equipment) => (
                    <TableRow key={equipment.id} data-testid={`equipment-row-${equipment.id}`}>
                      <TableCell className="font-medium">{equipment.name}</TableCell>
                      <TableCell className="font-mono text-sm">{equipment.tag}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{equipment.registration || '—'}</TableCell>
                      <TableCell>{equipment.equipment_type_name}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <Badge variant={equipment.status === 'online' ? 'default' : 'secondary'}>
                            {equipment.status === 'online' ? (
                              <><Wifi className="w-3 h-3 mr-1" /> {t('online')}</>
                            ) : (
                              <><WifiOff className="w-3 h-3 mr-1" /> {t('offline')}</>
                            )}
                          </Badge>
                          {equipment.status !== 'online' && equipment.last_seen && (
                            <span className="text-[10px] text-muted-foreground">
                              {formatDateTime(equipment.last_seen)}
                            </span>
                          )}
                        </div>
                        {/* v3.10.0: Badge de atualização do agent */}
                        <AgentUpdateBadge equipmentId={equipment.id} />
                      </TableCell>
                      <TableCell>
                        <Badge
                          data-testid={`op-status-badge-${equipment.id}`}
                          variant="outline"
                          className={cn(
                            equipment.operational_status === 'active' && 'border-green-500/50 text-green-400',
                            equipment.operational_status === 'maintenance' && 'border-yellow-500/50 text-yellow-400',
                            equipment.operational_status === 'inactive' && 'border-gray-500/50 text-gray-400',
                          )}
                        >
                          {equipment.operational_status === 'active' && 'Ativo'}
                          {equipment.operational_status === 'maintenance' && 'Manutenção'}
                          {equipment.operational_status === 'inactive' && 'Inativo'}
                          {!equipment.operational_status && 'Ativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-wrap gap-1 justify-end">
                          {/* D3 FIX: Botão único "Monitorar" substituiu "Live" e "Dashboard" */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 gap-1"
                            onClick={() => navigate(`/equipments/${equipment.id}/monitor`)}
                            title="Monitorar Equipamento"
                            data-testid={`monitor-equipment-${equipment.id}`}
                          >
                            <Activity className="w-4 h-4 text-green-500" />
                            <span className="text-xs hidden lg:inline">Monitorar</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 gap-1"
                            onClick={() => navigate(`/equipments/${equipment.id}/monitor?tab=historico`)}
                            title="Histórico"
                            data-testid={`history-equipment-${equipment.id}`}
                          >
                            <History className="w-4 h-4 text-cyan-500" />
                            <span className="text-xs hidden lg:inline">Hist</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 gap-1"
                            onClick={() => navigate(`/equipments/${equipment.id}/sources`)}
                            title="Configurar Sources (FASE 2)"
                            data-testid={`sources-equipment-${equipment.id}`}
                          >
                            <Settings2 className="w-4 h-4 text-orange-500" />
                            <span className="text-xs hidden lg:inline">Sources</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 gap-1"
                            onClick={() => setReprovisioningEquipment(equipment)}
                            title="Reprovisionar (Wizard)"
                            data-testid={`reprovision-equipment-${equipment.id}`}
                          >
                            <RefreshCw className="w-4 h-4 text-purple-500" />
                            <span className="text-xs hidden lg:inline">Wizard</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 gap-1"
                            onClick={() => setMqttCredentialsEquipment(equipment)}
                            title={t('mqttCredentials')}
                            data-testid={`mqtt-equipment-${equipment.id}`}
                          >
                            <Key className="w-4 h-4 text-blue-500" />
                            <span className="text-xs hidden lg:inline">MQTT</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 gap-1"
                            onClick={() => navigate(`/equipments/${equipment.id}/alerts`)}
                            title="Configurar alertas"
                            data-testid={`alerts-equipment-${equipment.id}`}
                          >
                            <Bell className="w-4 h-4 text-yellow-500" />
                            <span className="text-xs hidden lg:inline">Alertas</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 gap-1"
                            onClick={() => setGeofenceEquipment(equipment)}
                            title="Configurar Geofences"
                            data-testid={`geofence-equipment-${equipment.id}`}
                          >
                            <Hexagon className="w-4 h-4 text-cyan-500" />
                            <span className="text-xs hidden lg:inline">Geofence</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 gap-1"
                            onClick={() => setGpsSourceEquipment(equipment)}
                            title="Configurar fonte de GPS"
                            data-testid={`gps-equipment-${equipment.id}`}
                          >
                            <MapPin className="w-4 h-4 text-green-500" />
                            <span className="text-xs hidden lg:inline">GPS</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 gap-1"
                            onClick={() => setAgentUpdateEquipment(equipment)}
                            title="Verificar atualização do Agent"
                            data-testid={`agent-equipment-${equipment.id}`}
                          >
                            <Package className="w-4 h-4 text-purple-500" />
                            <span className="text-xs hidden lg:inline">Agent</span>
                          </Button>
                          {equipment.provisioning_fingerprint && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 gap-1"
                              onClick={() => {
                                setSelectedEquipment(equipment);
                                setUnlinkDialogOpen(true);
                              }}
                              title="Desvincular dispositivo"
                              data-testid={`unlink-equipment-${equipment.id}`}
                            >
                              <Unplug className="w-4 h-4 text-orange-500" />
                              <span className="text-xs hidden lg:inline">Unlink</span>
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 gap-1"
                            onClick={() => handleOpenDialog(equipment)}
                            title="Editar dados básicos"
                            data-testid={`edit-equipment-${equipment.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                            <span className="text-xs hidden lg:inline">Editar</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 gap-1 text-destructive"
                            onClick={() => {
                              setSelectedEquipment(equipment);
                              setDeleteDialogOpen(true);
                            }}
                            title="Excluir"
                            data-testid={`delete-equipment-${equipment.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                            <span className="text-xs hidden lg:inline">Excluir</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Seção: Aguardando Conexão Inicial (Feature 2) */}
      {awaitingConnectionEquipments.length > 0 && (
        <Card className="border-yellow-500/30 bg-yellow-500/5">
          <CardHeader className="py-4">
            <CardTitle className="text-lg flex items-center gap-2 text-yellow-500">
              <WifiOff className="w-5 h-5" />
              Aguardando Conexão Inicial ({awaitingConnectionEquipments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="w-full">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>{t('equipmentName')}</TableHead>
                    <TableHead>{t('equipmentTag')}</TableHead>
                    <TableHead>{t('equipmentType')}</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {awaitingConnectionEquipments.map((equipment) => {
                    // Verificar se última atualização foi recente (dispositivo conectou)
                    const lastSeen = equipment.last_seen ? new Date(equipment.last_seen) : null;
                    const now = new Date();
                    const isRecentlyActive = lastSeen && (now - lastSeen) < 60000; // 60 segundos
                    
                    return (
                      <TableRow key={equipment.id} data-testid={`awaiting-equipment-row-${equipment.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {isRecentlyActive ? (
                              <>
                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                <span className="text-xs text-green-500">Conectado</span>
                              </>
                            ) : (
                              <>
                                <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                                <span className="text-xs text-yellow-500">Aguardando</span>
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{equipment.name}</TableCell>
                        <TableCell className="font-mono text-sm">{equipment.tag}</TableCell>
                        <TableCell>{equipment.equipment_type_name || '—'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {equipment.created_at ? formatDate(equipment.created_at) : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn(
                                "h-8 px-2 gap-1",
                                isRecentlyActive ? "text-green-500" : ""
                              )}
                              onClick={() => setReprovisioningEquipment(equipment)}
                              title={isRecentlyActive ? "Concluir provisionamento" : "Continuar provisionamento"}
                              data-testid={`continue-provision-${equipment.id}`}
                            >
                              <RefreshCw className={cn(
                                "w-4 h-4",
                                isRecentlyActive ? "text-green-500" : "text-yellow-500"
                              )} />
                              <span className="text-xs">{isRecentlyActive ? "Concluir" : "Continuar"}</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 gap-1 text-destructive"
                              onClick={() => {
                                setSelectedEquipment(equipment);
                                setDeleteDialogOpen(true);
                              }}
                              title="Excluir"
                              data-testid={`delete-awaiting-${equipment.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>
              {selectedEquipment ? t('editEquipment') : t('addEquipment')}
            </DialogTitle>
          </DialogHeader>

          {/* Phase 999.4 Plan 03 (D-01/D-02/D-03): diálogo em ABAS — conteúdo só
              da aba ativa (Radix TabsContent desmonta o inativo, o que também
              barateia o fetch dos componentes autônomos Motores/Telemetria/
              Operação), cada aba com seu próprio Salvar. */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="identificacao" data-testid="equip-tab-identificacao">Identificação</TabsTrigger>
              <TabsTrigger value="motores" data-testid="equip-tab-motores">Motores</TabsTrigger>
              <TabsTrigger value="tanques" data-testid="equip-tab-tanques">Tanques</TabsTrigger>
              <TabsTrigger value="carga" data-testid="equip-tab-carga">Carga</TabsTrigger>
              <TabsTrigger value="telemetria" data-testid="equip-tab-telemetria">Telemetria (Ao vivo)</TabsTrigger>
              <TabsTrigger value="operacao" data-testid="equip-tab-operacao">Operação</TabsTrigger>
              <TabsTrigger value="dispositivo" data-testid="equip-tab-dispositivo">Dispositivo</TabsTrigger>
            </TabsList>

            {/* ABA: Identificação (default) — no_telemetry NO TOPO (condiciona as
                demais abas), depois nome/tipo/tags/registro/status/localização/
                descrição. Salvar = handleSave (PUT equipments). */}
            <TabsContent value="identificacao" className="grid gap-4 py-4">
              {/* Equipamento sem telemetria (D-13): viagens lançadas manualmente */}
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div className="space-y-0.5 pr-4">
                  <Label htmlFor="no_telemetry">Equipamento sem telemetria</Label>
                  <p className="text-xs text-muted-foreground">
                    Equipamento real que não possui telemetria embarcada. As viagens são lançadas
                    manualmente (horímetro, combustível e tempo), sem coleta automática.
                  </p>
                </div>
                <Switch
                  id="no_telemetry"
                  checked={formData.no_telemetry}
                  onCheckedChange={(checked) => setFormData({ ...formData, no_telemetry: checked })}
                  data-testid="equipment-no-telemetry-switch"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('equipmentName')}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Rebocador Areia I"
                    data-testid="equipment-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('equipmentType')}</Label>
                  <Select
                    value={formData.equipment_type_id}
                    onValueChange={(value) => setFormData({ ...formData, equipment_type_id: value })}
                  >
                    <SelectTrigger data-testid="equipment-type-select">
                      <SelectValue placeholder={t('equipmentType')} />
                    </SelectTrigger>
                    <SelectContent>
                      {equipmentTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* TAG IoT and TAG Senior */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>TAG IoT</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={selectedEquipment?.tag || 'Auto-gerado'}
                      disabled
                      className="bg-muted text-muted-foreground"
                      data-testid="equipment-tag-iot-display"
                    />
                    {selectedEquipment && (
                      <Badge variant="outline" className="whitespace-nowrap">Auto</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {selectedEquipment
                      ? 'TAG gerada automaticamente com base no tipo de equipamento'
                      : 'Será gerada automaticamente ao salvar'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tag_senior">TAG SENIOR/SISMETRO</Label>
                  <Input
                    id="tag_senior"
                    value={formData.tag_senior}
                    onChange={(e) => setFormData({ ...formData, tag_senior: e.target.value })}
                    placeholder="Ex: REB001, 12345"
                    data-testid="equipment-tag-senior-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    TAG para integração com sistema SENIOR/SISMETRO
                  </p>
                </div>
              </div>

              {/* Fase 2: Registration + ERP + Operational Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="registration">Registro / Matrícula</Label>
                  <Input
                    id="registration"
                    value={formData.registration}
                    onChange={(e) => setFormData({ ...formData, registration: e.target.value })}
                    placeholder="Ex: REB-2024-001"
                    data-testid="equipment-registration-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="erp_integration_id">ID ERP</Label>
                  <Input
                    id="erp_integration_id"
                    value={formData.erp_integration_id}
                    onChange={(e) => setFormData({ ...formData, erp_integration_id: e.target.value })}
                    placeholder="Ex: SENIOR-45021"
                    data-testid="equipment-erp-id-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Status Operacional</Label>
                <Select
                  value={formData.operational_status}
                  onValueChange={(value) => setFormData({ ...formData, operational_status: value })}
                >
                  <SelectTrigger data-testid="operational-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="maintenance">Manutenção</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                  </SelectContent>
                </Select>
                {formData.operational_status === 'maintenance' && (
                  <p className="text-xs text-yellow-500">Alertas serão suprimidos automaticamente durante manutenção</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t('locationType')}</Label>
                <Select
                  value={formData.location_type}
                  onValueChange={(value) => setFormData({ ...formData, location_type: value })}
                >
                  <SelectTrigger data-testid="location-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gps">{t('gpsLocation')}</SelectItem>
                    <SelectItem value="fixed">{t('fixedLocation')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.location_type === 'fixed' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="latitude">{t('latitude')}</Label>
                      <Input
                        id="latitude"
                        type="number"
                        step="any"
                        value={formData.latitude || ''}
                        onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) || null })}
                        data-testid="latitude-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="longitude">{t('longitude')}</Label>
                      <Input
                        id="longitude"
                        type="number"
                        step="any"
                        value={formData.longitude || ''}
                        onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) || null })}
                        data-testid="longitude-input"
                      />
                    </div>
                  </div>

                  <LocationPicker
                    position={formData.latitude && formData.longitude ? [formData.latitude, formData.longitude] : null}
                    onPositionChange={([lat, lng]) => setFormData({ ...formData, latitude: lat, longitude: lng })}
                  />
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="description">{t('description')}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="description-input"
                />
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={handleSave} disabled={saving} data-testid="save-equipment-btn">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {t('save')}
                </Button>
              </div>
            </TabsContent>

            {/* ABA: Motores (D-03) — cadastro físico (papel/posição/regimes/modelo);
                split do LiveTelemetryConfig (Plan 03 Task 1). Save próprio do
                componente. */}
            <TabsContent value="motores" className="py-4">
              {!selectedEquipment ? (
                <p className="text-sm text-muted-foreground">Salve o equipamento primeiro para cadastrar motores.</p>
              ) : formData.no_telemetry ? (
                <p className="text-sm text-muted-foreground">Equipamento sem telemetria — não há motores a cadastrar via telemetria.</p>
              ) : (
                <MotorsConfig equipmentId={selectedEquipment.id} />
              )}
            </TabsContent>

            {/* ABA: Tanques (D-06/D-07/D-13) — gateada por uses_fuel; formulário
                de capacidade/reserva/sensor/detector via <TankConfig/>. */}
            <TabsContent value="tanques" className="py-4">
              {!selectedEquipment ? (
                <p className="text-sm text-muted-foreground">Salve o equipamento primeiro para configurar o tanque.</p>
              ) : usesFuel ? (
                <TankConfig equipmentId={selectedEquipment.id} />
              ) : (
                <p className="text-sm text-muted-foreground">Este tipo de equipamento não usa combustível.</p>
              )}
            </TabsContent>

            {/* ABA: Carga — Modos de carga (CC-2026-07-02-005): alimentam o
                dropdown de modo e o volume previsto na programação de viagens.
                Save compartilhado com Identificação (mesmo formData, RESEARCH
                Open Q3). */}
            <TabsContent value="carga" className="grid gap-2 py-4">
              <Label>Modos de carga (programação de viagens)</Label>
              <p className="text-xs text-muted-foreground">
                Ex.: "Comboiada" com 1.900 t. Sem cadastro, valem os modos padrão
                Escoteira (~950 t) / Comboiada (~1.900 t).
              </p>
              {(formData.cargo_modes || []).map((m, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={m.label || ''}
                    onChange={(e) => {
                      const modes = [...formData.cargo_modes];
                      modes[idx] = { ...modes[idx], label: e.target.value };
                      setFormData({ ...formData, cargo_modes: modes });
                    }}
                    placeholder="Nome do modo (ex.: Comboiada)"
                    data-testid={`cargo-mode-label-${idx}`}
                  />
                  <Input
                    type="number"
                    min="0"
                    className="w-36"
                    value={m.capacidade_t ?? ''}
                    onChange={(e) => {
                      const modes = [...formData.cargo_modes];
                      modes[idx] = { ...modes[idx], capacidade_t: e.target.value };
                      setFormData({ ...formData, cargo_modes: modes });
                    }}
                    placeholder="t"
                    data-testid={`cargo-mode-cap-${idx}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormData({
                      ...formData,
                      cargo_modes: formData.cargo_modes.filter((_, i) => i !== idx),
                    })}
                  >
                    Remover
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setFormData({
                  ...formData,
                  cargo_modes: [...(formData.cargo_modes || []), { label: '', capacidade_t: '' }],
                })}
                data-testid="cargo-mode-add"
              >
                + Adicionar modo de carga
              </Button>

              <div className="flex justify-end pt-2">
                <Button onClick={handleSave} disabled={saving} data-testid="save-equipment-cargo-btn">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {t('save')}
                </Button>
              </div>
            </TabsContent>

            {/* ABA: Telemetria (Ao vivo) — item 6.17, D-03: SÓ visibilidade de
                sinais (live_visible); split do LiveTelemetryConfig (Plan 03
                Task 1). Save próprio do componente. */}
            <TabsContent value="telemetria" className="py-4">
              {!selectedEquipment ? (
                <p className="text-sm text-muted-foreground">Salve o equipamento primeiro para configurar a telemetria.</p>
              ) : formData.no_telemetry ? (
                <p className="text-sm text-muted-foreground">Equipamento sem telemetria — não há sinais a exibir.</p>
              ) : (
                <SignalsConfig equipmentId={selectedEquipment.id} />
              )}
            </TabsContent>

            {/* ABA: Operação — ClassifierSettings drop-in (já autônomo, RC-2):
                set-point da histerese de saída por equipamento. Save próprio. */}
            <TabsContent value="operacao" className="py-4">
              {!selectedEquipment ? (
                <p className="text-sm text-muted-foreground">Salve o equipamento primeiro para configurar a operação.</p>
              ) : (
                <ClassifierSettings equipmentId={selectedEquipment.id} />
              )}
            </TabsContent>

            {/* ABA: Dispositivo — MQTT topic/client-id read-only. */}
            <TabsContent value="dispositivo" className="py-4">
              {!selectedEquipment ? (
                <p className="text-sm text-muted-foreground">Salve o equipamento primeiro para ver os dados do dispositivo.</p>
              ) : (
                <div className="grid grid-cols-2 gap-4 p-3 bg-muted/50 rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">MQTT Topic</Label>
                    <p className="text-sm font-mono">{selectedEquipment.mqtt_topic}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">MQTT Client ID</Label>
                    <p className="text-sm font-mono">{selectedEquipment.mqtt_client_id}</p>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            {/* Cada aba tem o SEU Salvar (D-02) — o rodapé só fecha o diálogo.
                Concluir = fechei, o que salvei está salvo; Cancelar = fecha
                descartando qualquer edição NÃO salva da aba aberta. */}
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={() => setDialogOpen(false)} data-testid="equipment-dialog-done">
              Concluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteEquipment')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('confirmDelete')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unlink Device Dialog */}
      <AlertDialog open={unlinkDialogOpen} onOpenChange={setUnlinkDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desvincular Dispositivo</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja desvincular o dispositivo físico (Raspberry Pi) do equipamento{' '}
              <strong>{selectedEquipment?.name}</strong>?
              <br /><br />
              O equipamento ficará offline e o dispositivo poderá ser vinculado a outro equipamento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnlinkDevice} className="bg-orange-600 hover:bg-orange-700">
              <Unplug className="w-4 h-4 mr-2" />
              Desvincular
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* MQTT Credentials Modal */}
      {mqttCredentialsEquipment && (
        <MQTTCredentialsModal
          equipmentId={mqttCredentialsEquipment.id}
          equipmentName={mqttCredentialsEquipment.name}
          onClose={() => setMqttCredentialsEquipment(null)}
        />
      )}

      {/* Provisioning Modal */}
      {provisioningEquipment && (
        <ProvisioningModal
          equipment={provisioningEquipment}
          onClose={() => setProvisioningEquipment(null)}
        />
      )}

      {/* Provisioning Wizard - New Equipment */}
      <ProvisioningWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onComplete={(equipmentId) => {
          fetchData();
          toast.success('Equipamento provisionado com sucesso!');
        }}
      />

      {/* Provisioning Wizard - Reprovisioning */}
      {reprovisioningEquipment && (
        <ProvisioningWizard
          open={!!reprovisioningEquipment}
          onClose={() => setReprovisioningEquipment(null)}
          existingEquipment={reprovisioningEquipment}
          onComplete={(equipmentId) => {
            fetchData();
            setReprovisioningEquipment(null);
          }}
        />
      )}

      {/* GPS Source Selector (v3.10.0) */}
      {gpsSourceEquipment && (
        <GPSSourceSelector
          equipmentId={gpsSourceEquipment.id}
          equipmentTag={gpsSourceEquipment.tag}
          isOpen={!!gpsSourceEquipment}
          onClose={() => setGpsSourceEquipment(null)}
          onSaved={fetchData}
        />
      )}

      {/* Agent Update Modal (v3.10.0) */}
      {agentUpdateEquipment && (
        <AgentUpdateModal
          equipmentId={agentUpdateEquipment.id}
          equipmentTag={agentUpdateEquipment.tag}
          isOpen={!!agentUpdateEquipment}
          onClose={() => setAgentUpdateEquipment(null)}
        />
      )}

      {/* Geofence Config Modal (v3.11.0) */}
      {geofenceEquipment && (
        <EquipmentGeofenceConfig
          equipmentId={geofenceEquipment.id}
          equipmentName={geofenceEquipment.name}
          isOpen={!!geofenceEquipment}
          onClose={() => setGeofenceEquipment(null)}
        />
      )}
    </div>
  );
};

export default EquipmentsPage;
